import { useEffect, useState, type ReactNode } from "react";
import axios, { type AxiosInstance } from "axios";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  Mail,
  PenLine,
  RefreshCw,
  ShieldCheck,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { ensureCsrfToken } from "../api/csrf";
import { Button, TextField, cx } from "../components/charte";
import { sameSignerName } from "../lib/signerName";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  withCredentials: true,
});

// Démonstration locale (développement seulement) : /signer#demo, avec un faux contrat. Le module est hors dépôt ;
// absent, rien ne change.
type Demo = { install: (client: AxiosInstance, hash: URLSearchParams) => { token: string; kind: string } };
const demoModule = import.meta.env.DEV
  ? Object.values(import.meta.glob<Demo>("../dev-local/signature-demo.ts", { eager: true }))[0]
  : undefined;
const demoHash = new URLSearchParams(window.location.hash.slice(1));
const demo = demoModule && demoHash.has("demo") ? demoModule.install(api, demoHash) : undefined;

type View = {
  requestId: string;
  /** Absent pour un contrat. */
  documentKind?: string;
  documentName: string;
  documentHash: string;
  expiresAt: string;
  state: string;
  signer: {
    name: string;
    role: "student" | "guardian" | "school";
    state: string;
  };
  consents: {
    read: string;
    electronic: string;
    guardian: string;
    school: string;
  };
};
const roleName = {
  student: "Élève",
  guardian: "Représentant légal",
  school: "Responsable de l’auto-école",
};
/** Libellés de la page selon le document présenté. */
const WORDING = {
  contract: {
    header: "Permio · Signature de contrat",
    title: "Consultez votre contrat avant de signer",
    reopen:
      "Rouvrez le lien personnel reçu par e-mail pour accéder au contrat.",
    consult: "Consulter mon contrat",
    read: "Lire le contrat",
    download: "Télécharger le contrat complet",
    frame: "Contrat complet et annexes",
    signed: "Contrat signé",
    sign: "Signer ce contrat",
    back: "Revenir au contrat",
    file: "contrat",
  },
  quote: {
    header: "Permio · Signature de devis",
    title: "Consultez votre devis avant de signer",
    reopen: "Rouvrez le lien personnel reçu par e-mail pour accéder au devis.",
    consult: "Consulter mon devis",
    read: "Lire le devis",
    download: "Télécharger le devis complet",
    frame: "Devis complet",
    signed: "Devis signé",
    sign: "Signer ce devis",
    back: "Revenir au devis",
    file: "devis",
  },
  order: {
    header: "Permio · Signature de commande",
    title: "Consultez votre commande avant de signer",
    reopen:
      "Rouvrez le lien personnel reçu par e-mail pour accéder à la commande.",
    consult: "Consulter ma commande",
    read: "Lire la commande",
    download: "Télécharger la commande complète",
    frame: "Commande complète",
    signed: "Commande signée",
    sign: "Signer cette commande",
    back: "Revenir à la commande",
    file: "commande",
  },
};
type Kind = keyof typeof WORDING;
const kindOf = (value?: string | null): Kind =>
  value === "quote" || value === "order" ? value : "contract";

// Refonte du 21/10/2026 : parcours en trois étapes (code reçu par e-mail, lecture, signature) ; sur téléphone,
// le document s'ouvre en plein écran (un PDF ne s'affiche pas dans la page sur la plupart des téléphones) ;
// le nom recopié ne tient compte ni des majuscules ni des accents. Appels au serveur inchangés.
const STEPS = ["Code", "Document", "Signature"];

function Steps({ current }: { current: number }) {
  return (
    <ol className="grid grid-cols-3 gap-2" aria-label={`Étape ${current + 1} sur ${STEPS.length}`}>
      {STEPS.map((label, i) => (
        <li key={label} aria-current={i === current ? "step" : undefined}>
          <span className={cx("block h-1.5 rounded-pill transition-colors", i <= current ? "bg-primary" : "bg-primary-100")} />
          <span
            className={cx(
              "mt-1.5 flex items-center gap-1 text-xs font-medium",
              i === current ? "text-primary-700" : i < current ? "text-secondary" : "text-muted",
            )}
          >
            {i < current && <Check size={12} aria-hidden />}
            {i + 1}. {label}
          </span>
        </li>
      ))}
    </ol>
  );
}

const card = "rounded-card bg-white p-6 shadow-card max-sm:p-5";
const linkButton =
  "inline-flex h-11 items-center justify-center gap-2 rounded-pill border border-line bg-white px-5 text-sm font-medium text-body transition-all hover:border-primary-200 hover:shadow-navbar [&_svg]:text-primary";

/** Pastille d'icône en tête d'un bloc. */
function Tile({ icon: Icon, tone = "indigo" }: { icon: LucideIcon; tone?: "indigo" | "green" | "red" }) {
  return (
    <span
      className={cx(
        "grid h-11 w-11 shrink-0 place-items-center rounded-2xl",
        tone === "green" ? "bg-success-light text-success-dark" : tone === "red" ? "bg-danger-light text-danger-dark" : "bg-surface-indigo text-primary",
      )}
    >
      <Icon size={20} aria-hidden />
    </span>
  );
}

/** Consentement à cocher : toute la carte est cliquable. */
function Consent({ checked, onChange, children }: { checked: boolean; onChange: (value: boolean) => void; children: ReactNode }) {
  return (
    <label
      className={cx(
        "flex cursor-pointer items-start gap-3 rounded-row border p-4 text-sm leading-relaxed transition-colors",
        checked ? "border-primary-300 bg-surface-indigo text-heading" : "border-line bg-white text-body hover:border-primary-200",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-primary"
      />
      <span>{children}</span>
    </label>
  );
}

const backToPortal = (
  <a className="text-sm font-medium text-primary-600 underline-offset-[3px] hover:underline" href="/">
    Retourner à mon espace élève
  </a>
);

export default function ContractSigningPage() {
  // Lien reçu par e-mail (« token ») ou code demandé depuis l'espace élève (« challenge »).
  const [access] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)),
  );
  const invite = access.get("token") ?? demo?.token ?? "";
  const fromPortal = !invite && access.has("challenge");
  const [kind, setKind] = useState<Kind>(() => kindOf(access.get("kind") ?? demo?.kind));
  const w = WORDING[kind];
  const [challenge, setChallenge] = useState(
      () => access.get("challenge") ?? "",
    ),
    [destination, setDestination] = useState(() => access.get("email") ?? ""),
    [code, setCode] = useState("");
  const [session, setSession] = useState(""),
    [view, setView] = useState<View | null>(null),
    [pdf, setPdf] = useState(""),
    [stage, setStage] = useState<"document" | "sign">("document");
  const [read, setRead] = useState(false),
    [electronic, setElectronic] = useState(false),
    [authority, setAuthority] = useState(false),
    [name, setName] = useState("");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [declineReview, setDeclineReview] = useState(false);
  useEffect(() => {
    window.history.replaceState({}, "", window.location.pathname);
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
  useEffect(
    () => () => {
      if (pdf) URL.revokeObjectURL(pdf);
    },
    [pdf],
  );
  // Chaque étape repart du haut de la page (téléphone surtout).
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [stage, session]);
  const headers = (credential = session) => ({
    Authorization: `Bearer ${credential}`,
  });
  async function post(path: string, data: unknown, credential = session) {
    const csrf = await ensureCsrfToken();
    return api.post(`/v1/contract-signing/${path}`, data, {
      headers: { ...headers(credential), "x-csrf-token": csrf ?? "" },
    });
  }
  async function task(run: () => Promise<void>) {
    setBusy(true);
    setError("");
    try {
      await run();
    } catch (e) {
      setError(
        axios.isAxiosError(e)
          ? String(
              e.response?.data?.message ?? "Opération indisponible. Réessayez.",
            )
          : "Opération indisponible.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function load(credential = session) {
    const { data } = await api.get<View>("/v1/contract-signing/session", {
      headers: headers(credential),
    });
    setView(data);
    if (data.documentKind) setKind(kindOf(data.documentKind));
    if (!pdf) {
      const file = await api.get("/v1/contract-signing/document/original", {
        responseType: "blob",
        headers: headers(credential),
      });
      setPdf(URL.createObjectURL(file.data));
    }
  }
  async function download(kind: string) {
    const result = await api.get(`/v1/contract-signing/document/${kind}`, {
      responseType: "blob",
      headers: headers(),
    });
    const url = URL.createObjectURL(result.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${w.file}-${kind}.${kind === "evidence" ? "json" : "pdf"}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  const pending = !!view && view.state === "pending" && view.signer.state !== "signed";
  const step = !session ? 0 : stage === "document" ? 1 : 2;
  const nameOk = !!view && sameSignerName(name, view.signer.name);
  const nameWrong = !!view && !nameOk && name.trim().length >= view.signer.name.trim().length;
  const canSign =
    !busy && !!pdf && read && electronic && (view?.signer.role === "student" || authority) && nameOk;

  return (
    <main className="min-h-screen bg-hero-indigo px-4 py-8 text-body max-sm:py-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <img src="/logo-permio-eleve.png" alt="Permio" className="h-8 w-auto" />
        <header className="space-y-2">
          <p className="text-eyebrow uppercase text-primary-600">{w.header}</p>
          <h1 className="text-title-md text-heading sm:text-title-lg">
            {!view || pending
              ? w.title
              : view.state === "completed"
                ? w.signed
                : view.signer.state === "signed"
                  ? "Signature enregistrée"
                  : "Demande terminée"}
          </h1>
          {(!view || pending) && (
            <p className="text-sm text-secondary">
              Votre consentement est personnel. Vous pouvez télécharger le
              document ou refuser de le signer.
            </p>
          )}
        </header>
        {(!session || pending) && <Steps current={step} />}
        {error && (
          <p role="alert" className="flex items-start gap-2 rounded-row bg-danger-light p-4 text-sm text-danger-dark">
            <XCircle size={17} className="mt-px shrink-0" aria-hidden />
            {error}
          </p>
        )}

        {!session ? (
          <section className={cx(card, "space-y-5")}>
            {!invite && !challenge ? (
              <div className="flex items-start gap-4">
                <Tile icon={Mail} />
                <div className="space-y-2">
                  {fromPortal ? (
                    <>
                      <p className="text-[15px] text-body">Demandez un nouveau code depuis votre espace élève.</p>
                      {backToPortal}
                    </>
                  ) : (
                    <p className="text-[15px] text-body">{w.reopen}</p>
                  )}
                </div>
              </div>
            ) : !challenge ? (
              <>
                <div className="flex items-start gap-4">
                  <Tile icon={Mail} />
                  <div>
                    <h2 className="text-title-sm text-heading">Vérifier votre adresse e-mail</h2>
                    <p className="mt-1 text-sm text-secondary">
                      Nous envoyons un code à six chiffres à l’adresse du signataire. Ouvrir ce lien ne signe aucun
                      document.
                    </p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  icon={Mail}
                  className="h-11 max-sm:w-full"
                  loading={busy}
                  disabled={busy}
                  onClick={() =>
                    void task(async () => {
                      const { data } = await post("access", { token: invite });
                      setChallenge(data.challengeId);
                      setDestination(data.email);
                    })
                  }
                >
                  Recevoir mon code
                </Button>
              </>
            ) : (
              <form
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  void task(async () => {
                    const { data } = await post("verify", {
                      challengeId: challenge,
                      code,
                    });
                    setSession(data.session);
                    setCode("");
                    setStage("document");
                    await load(data.session);
                  });
                }}
              >
                <div className="flex items-start gap-4">
                  <Tile icon={Mail} />
                  <div>
                    <h2 className="text-title-sm text-heading">Saisissez le code reçu</h2>
                    <p className="mt-1 text-sm text-secondary">
                      Code envoyé à {destination}. Il expire dans dix minutes.
                    </p>
                  </div>
                </div>
                <div className="max-w-xs">
                  <TextField
                    label="Code à six chiffres"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    required
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="h-12 text-center text-xl font-semibold tracking-[0.4em]"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                  <Button type="submit" variant="primary" className="h-11 max-sm:w-full" loading={busy} disabled={busy || code.length !== 6}>
                    {w.consult}
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    disabled={busy}
                    onClick={() => {
                      setChallenge("");
                      setCode("");
                    }}
                  >
                    Demander un autre code
                  </Button>
                </div>
              </form>
            )}
          </section>
        ) : (
          view && (
            <>
              {view.state === "completed" ? (
                <section className={cx(card, "space-y-5")}>
                  <div className="flex items-start gap-4">
                    <Tile icon={CheckCircle2} tone="green" />
                    <div>
                      <h2 className="text-title-sm text-heading">Toutes les signatures sont enregistrées</h2>
                      <p className="mt-1 text-sm text-secondary">
                        {view.documentName} est signé. Gardez une copie des documents ci-dessous.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {[
                      ["signed", w.signed],
                      ["certificate", "Attestation"],
                      ["evidence", "Dossier de preuve"],
                    ].map(([kind, label]) => (
                      <Button
                        key={kind}
                        icon={Download}
                        className="h-11 max-sm:w-full"
                        disabled={busy}
                        onClick={() => void task(() => download(kind))}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  {fromPortal && backToPortal}
                </section>
              ) : view.signer.state === "signed" ? (
                <section className={cx(card, "space-y-5")}>
                  <div className="flex items-start gap-4">
                    <Tile icon={CheckCircle2} tone="green" />
                    <div>
                      <h2 className="text-title-sm text-heading">Votre signature est enregistrée</h2>
                      <p className="mt-1 text-sm text-secondary">
                        Le document final sera disponible après les autres
                        signatures. Un e-mail vous donnera accès à votre copie.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                    <Button icon={RefreshCw} className="h-11" disabled={busy} onClick={() => void task(() => load())}>
                      Actualiser le suivi
                    </Button>
                    {fromPortal && backToPortal}
                  </div>
                </section>
              ) : !pending ? (
                <section className={cx(card, "flex items-start gap-4")}>
                  <Tile icon={XCircle} tone="red" />
                  <p className="pt-2.5 text-[15px] text-body">
                    Cette demande est terminée ou n’est plus disponible. Contactez
                    votre auto-école.
                  </p>
                </section>
              ) : stage === "document" ? (
                <section className={cx(card, "space-y-5")}>
                  <div className="flex items-start gap-4">
                    <Tile icon={FileText} />
                    <div className="min-w-0">
                      <h2 className="break-words text-title-sm text-heading">{view.documentName}</h2>
                      <p className="mt-1 text-sm text-secondary">
                        {view.signer.name} · {roleName[view.signer.role]}
                      </p>
                      {view.expiresAt && (
                        <p className="mt-0.5 text-xs text-muted">
                          À signer avant le {new Date(view.expiresAt).toLocaleDateString("fr-FR")}
                        </p>
                      )}
                    </div>
                  </div>
                  {pdf ? (
                    <>
                      {/* Téléphone : plein écran. Tablette et ordinateur : dans la page. */}
                      <a className={cx(linkButton, "w-full md:hidden")} href={pdf} target="_blank" rel="noopener">
                        <ExternalLink size={16} aria-hidden />
                        {w.read}
                      </a>
                      <iframe title={w.frame} src={pdf} className="hidden h-[65vh] w-full rounded-row border border-line md:block" />
                      <a
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 underline-offset-[3px] hover:underline"
                        href={pdf}
                        download={view.documentName}
                      >
                        <Download size={15} aria-hidden />
                        {w.download}
                      </a>
                    </>
                  ) : (
                    <p className="text-sm text-secondary">Chargement du document…</p>
                  )}
                  <details className="text-xs text-muted">
                    <summary className="cursor-pointer font-medium text-secondary">Détails techniques</summary>
                    <p className="mt-2 break-all">Empreinte du document présenté : {view.documentHash}</p>
                  </details>
                  <div className="border-t border-line pt-5">
                    <Button variant="primary" icon={PenLine} className="h-11 max-sm:w-full" disabled={!pdf} onClick={() => setStage("sign")}>
                      Continuer vers la signature
                    </Button>
                  </div>
                </section>
              ) : (
                <form
                  className={cx(card, "space-y-5")}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!canSign) return;
                    void task(async () => {
                      const { data } = await post("sign", {
                        // Nom reconnu (à la forme près) : on envoie celui de la demande, que la preuve garde.
                        name: view.signer.name,
                        documentHash: view.documentHash,
                        read,
                        electronic,
                        authority,
                      });
                      setView(data);
                    });
                  }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-title-sm text-heading">Votre consentement</h2>
                    <Button type="button" variant="link" icon={ArrowLeft} onClick={() => setStage("document")}>
                      {w.back}
                    </Button>
                  </div>
                  <div className="space-y-3">
                    <Consent checked={read} onChange={setRead}>
                      {view.consents.read}
                    </Consent>
                    <Consent checked={electronic} onChange={setElectronic}>
                      {view.consents.electronic}
                    </Consent>
                    {view.signer.role !== "student" && (
                      <Consent checked={authority} onChange={setAuthority}>
                        {view.signer.role === "guardian" ? view.consents.guardian : view.consents.school}
                      </Consent>
                    )}
                  </div>
                  <TextField
                    label="Saisissez votre nom complet"
                    autoComplete="name"
                    maxLength={200}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={view.signer.name}
                    required
                    className="h-12 text-[15px]"
                    error={nameWrong ? `Ce n’est pas le nom attendu. Recopiez : ${view.signer.name}` : undefined}
                    hint={
                      nameOk ? (
                        <span className="inline-flex items-center gap-1 font-medium text-success-dark">
                          <Check size={13} aria-hidden />
                          Nom reconnu
                        </span>
                      ) : (
                        `À recopier : ${view.signer.name}. Majuscules et accents facultatifs.`
                      )
                    }
                  />
                  <p className="flex items-start gap-2 text-xs leading-relaxed text-secondary">
                    <ShieldCheck size={15} className="mt-px shrink-0 text-primary" aria-hidden />
                    <span>
                      Votre consentement, la date serveur, votre adresse IP et les
                      informations du navigateur seront conservés dans le dossier
                      de preuve accessible aux parties. Procédé de signature
                      simple par {fromPortal ? "espace élève" : "lien"} et code
                      e-mail.
                    </span>
                  </p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-line pt-5">
                    <Button type="submit" variant="primary" icon={PenLine} className="h-11 max-sm:w-full" loading={busy} disabled={!canSign}>
                      {w.sign}
                    </Button>
                    <Button type="button" variant="link" className="!text-secondary" disabled={busy} onClick={() => setDeclineReview(true)}>
                      Refuser de signer
                    </Button>
                  </div>
                  {declineReview && (
                    <div className="space-y-3 rounded-row bg-warn-soft p-4 text-sm text-warn-fg">
                      <p>
                        Ce refus arrête cette demande pour tous les signataires.
                        Le document et les consentements déjà donnés restent
                        conservés.
                      </p>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                        <Button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void task(async () => {
                              await post("decline", {});
                              setView({ ...view, state: "declined" });
                            })
                          }
                        >
                          Confirmer mon refus
                        </Button>
                        <Button type="button" variant="link" onClick={() => setDeclineReview(false)}>
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              )}
            </>
          )
        )}
        <footer className="text-xs leading-relaxed text-secondary">
          Les signatures des personnes et le scellement technique du dossier
          sont distincts. Permio ne présente pas ce parcours comme une signature
          qualifiée.
        </footer>
      </div>
    </main>
  );
}
